// src/RecipeDB/RecetarioPage.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getRecetario, updateRecetario, addCoOwner, addSharedUser, enableLinkShare } from './recetarioService';
import { searchUsersByName } from '../utils/dbUtils';
import { useAuth } from '../Auth/AuthContext';
import { useToast } from '../context/ToastContext';
import SavedRecipeCard from './SavedRecipeCard';
import { useFavoriteRecipes } from '../hooks/useFavoriteRecipes';
import './RecetarioPage.css';

const RecetarioPage = () => {
  const { recetarioId } = useParams();
  const { userId }      = useAuth();
  const { showToast }   = useToast();
  const navigate        = useNavigate();

  const [recetario, setRecetario]   = useState(null);
  const [recipes, setRecipes]       = useState([]);
  const [authorName, setAuthorName] = useState('');
  const [editing, setEditing]       = useState(false);
  const [editNombre, setEditNombre] = useState('');
  const [editDesc, setEditDesc]     = useState('');
  const [editPublica, setEditPublica] = useState(false);
  const [coOwnerInput, setCoOwnerInput] = useState('');
  const [loading, setLoading]       = useState(true);

  // Share panel state
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [shareSearch, setShareSearch]       = useState('');
  const [shareResults, setShareResults]     = useState([]);
  const [selectedShareUser, setSelectedShareUser] = useState(null);
  const [shareAsCoOwner, setShareAsCoOwner] = useState(false);
  const [addingShare, setAddingShare]       = useState(false);
  const [sharedUsers, setSharedUsers]       = useState([]);
  const [followerIds, setFollowerIds]       = useState(null); // null = not loaded yet
  const searchTimerRef = useRef(null);

  const { removeFromFavorites } = useFavoriteRecipes(userId);

  const isOwner   = recetario?.ownerId === userId;
  const isCoOwner = recetario?.coOwnerIds?.includes(userId);
  const canEdit   = isOwner || isCoOwner;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getRecetario(recetarioId);
        if (!data) return;
        setRecetario(data);
        setEditNombre(data.nombre);
        setEditDesc(data.descripcion || '');
        setEditPublica(data.publica || false);

        // Nombre del autor
        const userSnap = await getDoc(doc(db, 'users', data.ownerId));
        if (userSnap.exists()) {
          setAuthorName(userSnap.data().userName || userSnap.data().name || '');
        }

        // Recetas del recetario
        const recetaPromises = (data.recetaIds || []).map(id =>
          getDoc(doc(db, 'recetas', id)).then(snap =>
            snap.exists() ? { ...snap.data(), id: snap.id } : null
          )
        );
        const loaded = (await Promise.all(recetaPromises)).filter(Boolean);
        setRecipes(loaded);

        // Usuarios con acceso (co-owners + shared)
        const allShared = [
          ...(data.coOwnerIds || []).map(id => ({ id, role: 'editor' })),
          ...(data.sharedWithIds || []).map(id => ({ id, role: 'lector' })),
        ];
        const usersWithNames = await Promise.all(
          allShared.map(async ({ id, role }) => {
            const snap = await getDoc(doc(db, 'users', id));
            const userName = snap.exists()
              ? (snap.data().userName || snap.data().name || id)
              : id;
            return { id, userName, role };
          })
        );
        setSharedUsers(usersWithNames);
      } catch (err) {
        console.error('Error al cargar el recetario:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [recetarioId]);

  const handleSaveEdit = async () => {
    try {
      await updateRecetario(recetarioId, {
        nombre: editNombre,
        descripcion: editDesc,
        publica: editPublica,
      });
      setRecetario(prev => ({ ...prev, nombre: editNombre, descripcion: editDesc, publica: editPublica }));
      setEditing(false);
      showToast('Recetario actualizado ✓', 'success');
    } catch {
      showToast('Error al actualizar', 'error');
    }
  };

  const handleAddCoOwner = async () => {
    if (!coOwnerInput.trim()) return;
    try {
      const q = query(collection(db, 'users'), where('userName', '==', coOwnerInput.trim()));
      const snap = await getDocs(q);
      if (snap.empty) { showToast('Usuario no encontrado', 'error'); return; }
      const targetId = snap.docs[0].id;
      await addCoOwner(recetarioId, targetId);
      showToast(`@${coOwnerInput} añadido como co-editor`, 'success');
      setCoOwnerInput('');
    } catch {
      showToast('Error al añadir co-editor', 'error');
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/recetario/${recetarioId}`;
    await navigator.clipboard.writeText(url);
    showToast('Enlace copiado 🔗', 'success');
  };

  // Cargar IDs de seguidores cuando se abre el panel (una sola vez)
  useEffect(() => {
    if (!showSharePanel || !isOwner || followerIds !== null) return;
    getDoc(doc(db, 'users', userId))
      .then(s => setFollowerIds(s.data()?.followers || []))
      .catch(() => setFollowerIds([]));
  }, [showSharePanel, isOwner, userId, followerIds]);

  // Activar enlace compartido la primera vez que el owner abre el panel
  useEffect(() => {
    if (!showSharePanel || !isOwner) return;
    if (recetario?.enlaceCompartido) return; // ya está activo
    enableLinkShare(recetarioId)
      .then(() => setRecetario(prev => prev ? { ...prev, enlaceCompartido: true } : prev))
      .catch(console.warn);
  }, [showSharePanel, isOwner, recetario?.enlaceCompartido, recetarioId]);

  // Autocomplete: usa searchUsersByName (busca en name y userName) igual que UserSearchBar
  useEffect(() => {
    if (shareSearch.length < 2 || selectedShareUser) { setShareResults([]); return; }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      const term = shareSearch.replace(/^@/, '');
      try {
        const users = await searchUsersByName(term);
        const followerSet = new Set(followerIds || []);
        const results = users
          .filter(u => (u.objectID || u.id) !== userId)
          .map(u => ({
            id: u.objectID || u.id,
            userName: u.userName || u.name || u.objectID || u.id,
          }));
        // Seguidores primero, resto después
        results.sort((a, b) => {
          const aF = followerSet.has(a.id) ? 0 : 1;
          const bF = followerSet.has(b.id) ? 0 : 1;
          return aF - bF;
        });
        setShareResults(results.slice(0, 8));
      } catch { setShareResults([]); }
    }, 280);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [shareSearch, selectedShareUser, followerIds, userId]);

  const handleAddSharedUser = async () => {
    if (!selectedShareUser) return;
    setAddingShare(true);
    try {
      if (shareAsCoOwner) {
        await addCoOwner(recetarioId, selectedShareUser.id, userId, recetario.nombre);
      } else {
        await addSharedUser(recetarioId, selectedShareUser.id, userId, recetario.nombre);
      }
      const role = shareAsCoOwner ? 'editor' : 'lector';
      setSharedUsers(prev => [
        ...prev.filter(u => u.id !== selectedShareUser.id),
        { ...selectedShareUser, role },
      ]);
      showToast(
        `@${selectedShareUser.userName} añadido como ${role === 'editor' ? 'editor' : 'lector'}`,
        'success'
      );
      setSelectedShareUser(null);
      setShareSearch('');
      setShareResults([]);
      setShareAsCoOwner(false);
    } catch {
      showToast('Error al compartir', 'error');
    } finally {
      setAddingShare(false);
    }
  };

  if (loading) return <div className="recetario-page recetario-page--loading">Cargando recetario…</div>;
  if (!recetario) return <div className="recetario-page recetario-page--empty">Recetario no encontrado.</div>;

  return (
    <div className="recetario-page">
      {/* Header */}
      <div className="recetario-page__header">
        <button className="recetario-page__back" onClick={() => navigate(-1)}>← Volver</button>

        {editing ? (
          <div className="recetario-page__edit-form">
            <input
              value={editNombre}
              onChange={e => setEditNombre(e.target.value)}
              placeholder="Nombre del recetario"
              maxLength={60}
            />
            <textarea
              value={editDesc}
              onChange={e => setEditDesc(e.target.value)}
              placeholder="Descripción"
              rows={2}
              maxLength={200}
            />
            <label className="recetario-page__public-toggle">
              <input type="checkbox" checked={editPublica} onChange={e => setEditPublica(e.target.checked)} />
              Recetario público
            </label>
            <div className="recetario-page__edit-actions">
              <button onClick={handleSaveEdit}>Guardar</button>
              <button className="recetario-page__cancel" onClick={() => setEditing(false)}>Cancelar</button>
            </div>

            {/* Co-owners */}
            {isOwner && (
              <div className="recetario-page__coowners">
                <p className="recetario-page__coowners-title">Añadir co-editor por nombre de usuario:</p>
                <div className="recetario-page__coowners-row">
                  <input
                    value={coOwnerInput}
                    onChange={e => setCoOwnerInput(e.target.value)}
                    placeholder="@usuario"
                  />
                  <button onClick={handleAddCoOwner}>Añadir</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="recetario-page__meta">
            <h1 className="recetario-page__title">{recetario.nombre}</h1>
            {recetario.descripcion && <p className="recetario-page__desc">{recetario.descripcion}</p>}
            <div className="recetario-page__info">
              <span>Por {authorName || recetario.ownerId}</span>
              <span>·</span>
              <span>{recipes.length} {recipes.length === 1 ? 'receta' : 'recetas'}</span>
              {recetario.publica && <span className="recetario-page__badge">🌍 Público</span>}
            </div>
            <div className="recetario-page__actions">
              <button
                className={`recetario-page__share${showSharePanel ? ' active' : ''}`}
                onClick={() => setShowSharePanel(v => !v)}
              >
                🔗 Compartir
              </button>
              {canEdit && (
                <button className="recetario-page__edit" onClick={() => setEditing(true)}>✏️ Editar</button>
              )}
            </div>

            {/* ── Panel compartir ────────────────────────────── */}
            {showSharePanel && (
              <div className="recetario-share-panel">

                {/* Copiar enlace */}
                <div className="recetario-share-panel__link-row">
                  <span className="recetario-share-panel__url">
                    {`${window.location.origin}/recetario/${recetarioId}`}
                  </span>
                  <button className="recetario-share-panel__copy" onClick={handleCopyLink}>
                    Copiar
                  </button>
                </div>

                {/* Añadir usuarios — solo owner */}
                {isOwner && (
                  <>
                    <div className="recetario-share-panel__divider" />
                    <p className="recetario-share-panel__label">Compartir con un usuario</p>

                    <div className="recetario-share-panel__search-wrap">
                      <input
                        className="recetario-share-panel__search"
                        placeholder="Buscar por nombre de usuario..."
                        value={shareSearch}
                        onChange={e => {
                          setShareSearch(e.target.value);
                          setSelectedShareUser(null);
                        }}
                        autoComplete="off"
                      />
                      {shareResults.length > 0 && (
                        <ul className="recetario-share-panel__dropdown">
                          {shareResults.map(u => (
                            <li
                              key={u.id}
                              className="recetario-share-panel__result"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => {
                                setSelectedShareUser(u);
                                setShareSearch('@' + u.userName);
                                setShareResults([]);
                              }}
                            >
                              @{u.userName}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {selectedShareUser && (
                      <label className="recetario-share-panel__role-toggle">
                        <input
                          type="checkbox"
                          checked={shareAsCoOwner}
                          onChange={e => setShareAsCoOwner(e.target.checked)}
                        />
                        <span>Dar acceso de editor (puede añadir y quitar recetas)</span>
                      </label>
                    )}

                    <button
                      className="recetario-share-panel__add-btn"
                      onClick={handleAddSharedUser}
                      disabled={!selectedShareUser || addingShare}
                    >
                      {addingShare ? 'Añadiendo…' : '+ Añadir'}
                    </button>

                    {sharedUsers.length > 0 && (
                      <div className="recetario-share-panel__shared-list">
                        <p className="recetario-share-panel__shared-title">Ya compartido con:</p>
                        {sharedUsers.map(u => (
                          <div key={u.id} className="recetario-share-panel__shared-user">
                            <span>@{u.userName}</span>
                            <span className={`recetario-share-panel__badge recetario-share-panel__badge--${u.role}`}>
                              {u.role === 'editor' ? 'Editor' : 'Lector'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recetas */}
      {recipes.length === 0 ? (
        <p className="recetario-page__empty">Este recetario aún no tiene recetas.</p>
      ) : (
        <div className="feed">
          {recipes.map(recipe => (
            <SavedRecipeCard
              key={recipe.id}
              recipe={recipe}
              onRemove={removeFromFavorites}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RecetarioPage;
